import { useQuery } from '@tanstack/react-query';

import { withError } from '@/react-tools/react-query';
import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { VulnerabilityReportTable } from '../types';

import { queryKeys } from './queryKeys';

const TRIVY_API = 'apis/aquasecurity.github.io/v1alpha1';

// Projects the reports down to the CRD's printer columns instead of returning
// them whole. includeObject=Metadata brings back the labels that identify the
// workload behind each report.
const TABLE_ACCEPT = 'application/json;as=Table;v=v1;g=meta.k8s.io';

export function useVulnerabilityReportTable<T = VulnerabilityReportTable>({
  environmentId,
  enabled = true,
  select,
}: {
  environmentId: EnvironmentId;
  enabled?: boolean;
  select?: (table: VulnerabilityReportTable) => T;
}) {
  return useQuery(
    queryKeys.vulnerabilityReportTable(environmentId),
    () => getVulnerabilityReportTable(environmentId),
    {
      ...withError('Unable to get vulnerability reports'),
      enabled: enabled && !!environmentId,
      select,
    }
  );
}

async function getVulnerabilityReportTable(
  environmentId: EnvironmentId
): Promise<VulnerabilityReportTable> {
  try {
    const { data } = await axios.get<VulnerabilityReportTable>(
      `/endpoints/${environmentId}/kubernetes/${TRIVY_API}/vulnerabilityreports`,
      {
        headers: { Accept: TABLE_ACCEPT },
        params: { includeObject: 'Metadata' },
      }
    );
    return data;
  } catch (error) {
    throw parseAxiosError(error);
  }
}
